import {useMemo} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {StarRating} from 'UI/components/layout/StarRating';
import {Headline, P} from 'UI/components/layout/Text';

import type {WalkReviewSummary} from 'UI/utils/jsonLd';

const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {dateStyle: 'medium'});
const RATING_SUM_INITIAL = 0;

/**
 * The props for the `ReviewsSection` component.
 * Configures a visible list of a walk's approved reviews with an average-rating summary.
 */
interface ComponentProps {
    /** The section heading. */
    heading: string;
    /** Approved reviews for the walk (must be non-empty; caller should not render this component otherwise). */
    reviews: WalkReviewSummary[];
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'ReviewsSection'
     */
    testId?: string;
}

/**
 * A visible reviews section showing the average rating and every individual review.
 * This keeps the walk's `aggregateRating`/`review` JSON-LD compliant with Google's review
 * snippet guidelines, which require review content to be visibly present on the page.
 *
 * @param props         The component props.
 * @param props.heading The section heading text.
 * @param props.reviews Approved reviews for the walk.
 * @param props.testId  An optional test ID used for automation selectors. Defaults to `'ReviewsSection'`.
 * @returns A JSX element rendering the ReviewsSection.
 *
 * @example
 * ```tsx
 * <ReviewsSection heading="Bewertungen" reviews={reviews} />
 * ```
 */
const ReviewsSection = ({heading, reviews, testId = 'ReviewsSection'}: ComponentProps) => {
    const averageRating = useMemo(
        () => reviews.reduce((sum, review) => sum + review.rating, RATING_SUM_INITIAL) / reviews.length,
        [reviews]
    );

    return (
        <Wrapper data-cy={testId}>
            <SectionHeading $size="headline" as="h2">{heading}</SectionHeading>
            <StarRating count={reviews.length} rating={averageRating} testId={`${testId}-summary`} />
            <List>
                {reviews.map(review => (
                    <Item key={review.id} data-cy={`${testId}-item`}>
                        <ItemHeader>
                            <AuthorName $size="bodySmall" as="span">{review.authorName}</AuthorName>
                            <StarRating rating={review.rating} testId={`${testId}-item-rating`} />
                        </ItemHeader>
                        {review.text && <ReviewText $size="body" as="p">{review.text}</ReviewText>}
                        <DateLabel $size="labelSmall" as="span">
                            {DATE_FORMATTER.format(new Date(review.createdAt))}
                        </DateLabel>
                    </Item>
                ))}
            </List>
        </Wrapper>
    );
};

ReviewsSection.displayName = 'ReviewsSection';

export {ReviewsSection};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
`;

const SectionHeading = styled(Headline)`
    color: ${({theme}) => theme.colors.primaryDefault};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
`;

const Item = styled.div`
    background: ${({theme}) => theme.colors.surfaceHigh};
    border-radius: ${spacing(5)};
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    padding: ${spacing(5)};
`;

const ItemHeader = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
`;

const AuthorName = styled(P)`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-weight: 700;
`;

const ReviewText = styled(P)`
    line-height: 1.6;
`;

const DateLabel = styled(P)`
    color: ${({theme}) => theme.colors.inkTertiary};
`;